import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { IoArrowBackCircleSharp } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import Swal from "sweetalert2";
import countryPriceApi from "../../api/countryPriceApi";
import countries from "../../components/utils/countries";
import { currencyConfig } from "../../components/utils/currencyConfig";

const roleOptions = [
  { label: "L1 Admin", value: "L1_ADMIN" },
  { label: "L2 Admin", value: "L2_ADMIN" },
  { label: "Customer", value: "CUSTOMER" },
  { label: "All", value: "ALL" },
];

const AddCredits = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingPricing = location.state || null;
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [minPrice, setMinPrice] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Prepopulate form when editing
  useEffect(() => {
    if (editingPricing) {
      // Find country object for select
      const countryObj = countries.find(c => c.value === editingPricing.countryCode);
      setSelectedCountry(countryObj || null);
      setMinPrice(editingPricing.minPrice?.toString() || "");
      setSelectedRoles(editingPricing.visibleToRoles || []);
    }
  }, [editingPricing]);

  const handleBack = () => {
    navigate("/master-admin-dashboard/credit-management");
  };
  const handleSubmit = async () => {
    if (!selectedCountry || !minPrice || selectedRoles.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please select all required fields",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    let rolesToSend = selectedRoles;
    if (selectedRoles.includes("ALL")) {
      rolesToSend = ["L1_ADMIN", "L2_ADMIN", "CUSTOMER"];
    }
    const currencyData = currencyConfig[selectedCountry.value];
    const payload = {
      countryCode: selectedCountry.value,
      minPrice: parseFloat(minPrice),
      visibleToRoles: rolesToSend,
      currencyCode: currencyData.currency,
      currencySymbol: currencyData.symbol,
    };
    try {
      if (editingPricing) {
        // Update existing pricing
        await countryPriceApi.updateCountryPrice(editingPricing.id, payload);
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Country credit pricing updated successfully",
          confirmButtonColor: "#28a745",
        });
        navigate("/master-admin-dashboard/credit-management");
      } else {
        // Create new pricing
        await countryPriceApi.saveCreditPricing(payload);
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Country credit pricing added successfully",
          confirmButtonColor: "#28a745",
        });
        // reset form
        setSelectedCountry(null);
        setMinPrice("");
        setSelectedRoles([]);
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.response?.data || "Failed to save country pricing";
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    }
  };
  return (
    <div className="scrollable-container" style={{ height: "100%" }}>
      {/* Heading */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h1 className="mb-4">
          <strong>Add Country Credit Pricing</strong>
        </h1>
        <Button onClick={handleBack} variant="info" className="tooltip-btn">
          <IoArrowBackCircleSharp />
          <span className="tooltip-text">Go to Back</span>
        </Button>
      </div>

      <div className="card shadow p-3">
        {/* Country Select */}
        <Form.Group className="mb-3">
          <Form.Label>
            Country <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <CreatableSelect
            options={countries.map((c) => ({ label: c.label, value: c.value }))}
            value={selectedCountry}
            onChange={setSelectedCountry}
            placeholder="Select or type a country..."
            isClearable
          />
        </Form.Group>

        {/* Min Price */}
        <Form.Group className="mb-3">
          <Form.Label>
            Base Credit Price <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <Form.Control
            type="number"
            value={minPrice}
            min={0}
            onChange={(e) => setMinPrice(e.target.value)}
            placeholder="Enter base price"
          />
        </Form.Group>

        {/* Roles */}
        <Form.Group className="mb-3">
          <Form.Label>
            Visible Roles <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <CreatableSelect
            options={roleOptions}
            value={roleOptions.filter((r) => selectedRoles.includes(r.value))}
            onChange={(selected) =>
              setSelectedRoles(selected.map((s) => s.value))
            }
            placeholder="Select visible roles..."
            isMulti
          />
        </Form.Group>

        {/* Submit */}
        <div className="text-end">
          <Button variant="primary" onClick={handleSubmit}>
            Add Credit Pricing
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddCredits;
